import type {
    AnalysisOutput,
    DealAnalysisOutput,
    RfxAnalysisOutput,
    FlexibleAnalysisOutput,
} from "../../types/ProjectTypes";
import { Evaluation } from "../../types/ProjectTypes";
import { getScoreGrade } from "../../common/Grade";

/** Collapse runs of whitespace to a single space and trim. */
function collapse(text: string): string {
    return text.replace(/\s+/g, " ").trim();
}

/** Render an HTML <table> as a GitHub-flavored markdown table. */
function tableToMarkdown(table: Element): string {
    const rows = Array.from(table.querySelectorAll("tr"));
    const matrix = rows.map((row) =>
        Array.from(row.querySelectorAll("th,td")).map((cell) =>
            // Flatten each cell to inline text and escape pipes so the
            // markdown table stays well-formed.
            collapse(serializeChildren(cell)).replace(/\|/g, "\\|")
        )
    );
    if (!matrix.length) return "";

    const colCount = Math.max(...matrix.map((r) => r.length));
    const pad = (r: string[]): string[] => {
        while (r.length < colCount) r.push("");
        return r;
    };
    const fmt = (cells: string[]): string => `| ${cells.join(" | ")} |`;

    const header = pad(matrix[0]);
    const separator = header.map(() => "---");
    const body = matrix.slice(1).map(pad);

    return [fmt(header), fmt(separator), ...body.map(fmt)].join("\n");
}

/** Serialize a single DOM node to markdown-ish text. */
function serializeNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || "").replace(/\s+/g, " ");
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const el = node as Element;
    switch (el.tagName) {
        case "SCRIPT":
        case "STYLE":
        case "HEAD":
            return "";
        case "BR":
            return "\n";
        case "TABLE":
            return "\n\n" + tableToMarkdown(el) + "\n\n";
        case "UL":
        case "OL": {
            const ordered = el.tagName === "OL";
            const items = Array.from(el.children).filter(
                (c) => c.tagName === "LI"
            );
            const lines = items.map((li, i) => {
                const prefix = ordered ? `${i + 1}. ` : "- ";
                return prefix + collapse(serializeChildren(li));
            });
            return "\n" + lines.join("\n") + "\n";
        }
        case "H1":
        case "H2":
        case "H3":
        case "H4":
        case "H5":
        case "H6": {
            const level = Number(el.tagName[1]);
            return (
                "\n\n" +
                "#".repeat(level) +
                " " +
                collapse(serializeChildren(el)) +
                "\n\n"
            );
        }
        case "P":
        case "DIV":
        case "SECTION":
        case "ARTICLE":
        case "BLOCKQUOTE":
            return "\n\n" + serializeChildren(el) + "\n\n";
        default:
            // Inline elements (b, i, strong, em, span, a, …): emit children.
            return serializeChildren(el);
    }
}

/** Serialize all child nodes of an element. */
function serializeChildren(node: Node): string {
    let out = "";
    node.childNodes.forEach((child) => {
        out += serializeNode(child);
    });
    return out;
}

/**
 * Convert an HTML fragment to markdown-ish plain text for the LLM, preserving
 * tables (as GFM tables) and list/heading structure. Replaces the previous
 * `textContent`-based flattening that smashed table cells together.
 */
function htmlToMarkdown(html: string): string {
    const div = document.createElement("div");
    div.innerHTML = html;
    return serializeChildren(div)
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

/** "$1,234,000" from a USD-cents amount — matches the Summary tab's formatUsd. */
const formatUsdCents = (cents: number): string =>
    "$" + (cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 });

/** "12.3%" — matches the Summary tab's formatPercent (one decimal place). */
const formatPercent = (n: number): string =>
    n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";

/**
 * "Apr 1, 2026" from an ISO date string, read in UTC so a stored contract date
 * does not slide a day either way for a user west or east of the line.
 */
const formatIsoDate = (iso: string): string => {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("en-US", {
        timeZone: "UTC",
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const isKnownEvaluation = (e?: Evaluation): boolean =>
    e === Evaluation.FAVORABLE || e === Evaluation.NEUTRAL || e === Evaluation.NOT_FAVORABLE;

/** Overall deal-evaluation label, as shown on the Summary tab. */
const overallEvaluationLabel = (e?: Evaluation): string => {
    switch (e) {
        case Evaluation.FAVORABLE:
            return "Favorable";
        case Evaluation.NOT_FAVORABLE:
            return "Unfavorable";
        case Evaluation.NEUTRAL:
            return "Neutral";
        default:
            return "N/A";
    }
};

/** Sub-rating label, as shown on the Summary tab (Good / Weak / Neutral). */
const subEvaluationLabel = (e?: Evaluation): string => {
    switch (e) {
        case Evaluation.FAVORABLE:
            return "Good";
        case Evaluation.NOT_FAVORABLE:
            return "Weak";
        case Evaluation.NEUTRAL:
            return "Neutral";
        default:
            return "N/A";
    }
};

// Serialize a deal output into the same facts the user sees in the UI: the
// header deal score, the Summary tab's stat boxes / evaluation table, the
// Analysis tab's report sections, and the underlying negotiation levers.
// Sections are emitted only when they carry real data (no "N/A" filler), so a
// sparse output stays compact.
function serializeDeal(output: DealAnalysisOutput): string {
    const parts: string[] = [];
    const summary = output.summary;
    const dealInfo = output.dealInfo;
    const tcvCents = dealInfo?.value;

    // Deal score — shown in the analysis view header.
    if (dealInfo) {
        const scoreLines: string[] = [];
        if (typeof dealInfo.orderScore === "number") {
            scoreLines.push(
                `- Deal score: ${dealInfo.orderScore}% (${getScoreGrade(dealInfo.orderScore).grade})`
            );
        }
        if (
            typeof dealInfo.optimizedScore === "number" &&
            dealInfo.optimizedScore > dealInfo.orderScore
        ) {
            scoreLines.push(
                `- Optimized score: ${dealInfo.optimizedScore}% (${getScoreGrade(dealInfo.optimizedScore).grade})`
            );
        }
        if (typeof tcvCents === "number" && tcvCents > 0) {
            scoreLines.push(`- Total contract value: ${formatUsdCents(tcvCents)}`);
        }
        if (dealInfo.vendorName) {
            scoreLines.push(`- Vendor: ${dealInfo.vendorName}`);
        }
        // Term dates and auto-renewal are contract facts, not analysis: both
        // sides signed them. They used to be dropped here, which left the
        // assistant unable to say when the term runs to or whether it renews.
        const startDate = dealInfo.orderStartDate && formatIsoDate(dealInfo.orderStartDate);
        const endDate = dealInfo.orderEndDate && formatIsoDate(dealInfo.orderEndDate);
        if (startDate) scoreLines.push(`- Term start: ${startDate}`);
        if (endDate) scoreLines.push(`- Term end: ${endDate}`);
        if (typeof dealInfo.autoRenewal === "boolean") {
            scoreLines.push(`- Auto-renewal: ${dealInfo.autoRenewal ? "Yes" : "No"}`);
        }
        if (scoreLines.length) parts.push("## Deal Score\n" + scoreLines.join("\n"));
    }

    if (summary?.text) {
        parts.push("## Summary\n" + summary.text);
    }

    // Economic gain — the Summary tab's savings stat box (estimated for v1,
    // realized for v2+), with the same percent-of-contract-value framing.
    if (summary?.estimatedSavings) {
        const [low, high] = summary.estimatedSavings;
        let line = `Estimated savings: ${formatUsdCents(low)} – ${formatUsdCents(high)}`;
        if (typeof tcvCents === "number" && tcvCents > 0) {
            line +=
                ` (${formatPercent((low / tcvCents) * 100)} – ` +
                `${formatPercent((high / tcvCents) * 100)} cost reduction opportunity identified)`;
        }
        parts.push("## Economic Gain\n" + line);
    } else if (summary?.realizedSavings != null) {
        let line = `Realized savings: ${formatUsdCents(summary.realizedSavings)}`;
        if (typeof tcvCents === "number" && tcvCents > 0) {
            line += ` (${formatPercent((summary.realizedSavings / tcvCents) * 100)} reduction from baseline)`;
        }
        parts.push("## Economic Gain\n" + line);
    }

    // Strategic progress — the Summary tab's second stat box.
    const sp = summary?.strategicProgress;
    if (sp && (sp.prioritiesAchieved != null || sp.prioritiesIdentified > 0)) {
        parts.push(
            "## Strategic Progress\n" +
                (sp.prioritiesAchieved != null
                    ? `${sp.prioritiesAchieved} of ${sp.prioritiesIdentified} priorities achieved`
                    : `${sp.prioritiesIdentified} priorities identified`)
        );
    }

    if (summary?.implications?.length) {
        parts.push(
            "## Key Implications\n" +
                summary.implications
                    .map((i) => `- ${i.category}: ${i.implication}`)
                    .join("\n")
        );
    }

    // Deal evaluation — the Summary tab's overall + sub-rating table. Emitted
    // only when at least one rating is a recognized value.
    const evaluation = summary?.evaluation;
    if (
        evaluation &&
        (isKnownEvaluation(evaluation.overall) ||
            isKnownEvaluation(evaluation.pricing) ||
            isKnownEvaluation(evaluation.structuralFlexibility) ||
            isKnownEvaluation(evaluation.operationalAndLegalProtections) ||
            isKnownEvaluation(evaluation.strategicFit))
    ) {
        parts.push(
            "## Deal Evaluation\n" +
                [
                    `- Overall: ${overallEvaluationLabel(evaluation.overall)}`,
                    `- Pricing: ${subEvaluationLabel(evaluation.pricing)}`,
                    `- Structural flexibility: ${subEvaluationLabel(evaluation.structuralFlexibility)}`,
                    `- Operational / legal protections: ${subEvaluationLabel(evaluation.operationalAndLegalProtections)}`,
                    `- Strategic fit: ${subEvaluationLabel(evaluation.strategicFit)}`,
                ].join("\n")
        );
    }

    if (summary?.nextActions?.length) {
        parts.push(
            "## Recommended Actions\n" +
                summary.nextActions
                    .map((a) => `- ${a.action}: ${a.justification}`)
                    .join("\n")
        );
    }

    if (output.report?.contractSummary) {
        parts.push(
            "## Contract Summary\n" + htmlToMarkdown(output.report.contractSummary)
        );
    }
    if (output.report?.topLegalPriorities) {
        parts.push(
            "## Top Legal Priorities\n" +
                htmlToMarkdown(output.report.topLegalPriorities)
        );
    }
    if (output.report?.negotiationStrategy) {
        parts.push(
            "## Negotiation Strategy\n" +
                htmlToMarkdown(output.report.negotiationStrategy)
        );
    }

    // Negotiation levers — the structured data backing the analysis.
    if (output.levers?.length) {
        parts.push(
            "## Negotiation Levers\n" +
                output.levers
                    .map((l) => {
                        let head = `- ${l.name ?? l.type ?? "Lever"}`;
                        if (l.evaluation) head += ` [${l.evaluation}]`;
                        if (l.currentState) head += `: ${l.currentState}`;
                        if (l.isActionable && (l.optimalState || l.justification)) {
                            const sub = [
                                l.optimalState ? `target: ${l.optimalState}` : "",
                                l.justification ?? "",
                            ].filter(Boolean);
                            if (sub.length) head += `\n  - ${sub.join(" — ")}`;
                        }
                        return head;
                    })
                    .join("\n")
        );
    }

    return parts.join("\n\n");
}

function serializeRfx(output: RfxAnalysisOutput): string {
    if (output.report?.content) {
        return htmlToMarkdown(output.report.content);
    }
    return "";
}

function serializeFlexible(output: FlexibleAnalysisOutput): string {
    if (output.content) {
        return htmlToMarkdown(output.content);
    }
    return "";
}

export function serializeAnalysisOutput(
    output: AnalysisOutput | undefined
): string {
    if (!output) return "";

    switch (output.type) {
        case "deal_output":
            return serializeDeal(output as DealAnalysisOutput);
        case "rfx_output":
            return serializeRfx(output as RfxAnalysisOutput);
        case "flexible_output":
            return serializeFlexible(output as FlexibleAnalysisOutput);
        default:
            return "";
    }
}
